import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert, 
  Button,
  Drawer, 
  IconButton, 
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDialogCloseIconSx,
  harmonateFormFieldsSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateDrawerHeaderSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const DocumentTypeTermsDrawer = ({ 
  open, 
  onClose, 
  selectedDocumentType 
}) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.DOCUMENT_TYPE);
  const [termsData, setTermsData] = useState(null);
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsError, setTermsError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [termToDelete, setTermToDelete] = useState(null);
  const [termToDeleteType, setTermToDeleteType] = useState(null);
  const [newTerm, setNewTerm] = useState('');
  const [termType, setTermType] = useState('include'); // 'include' or 'exclude'
  const [editingTerm, setEditingTerm] = useState({ term: '', type: 'include' });
  const [submitting, setSubmitting] = useState(false);
  
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();

  const fetchTermsData = async (documentTypeId) => {
    try {
      setTermsLoading(true);
      setTermsError(null);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/${tenantId}/${documentTypeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) throw new Error(`Failed to fetch terms: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      setTermsData(data);
    } catch (err) {
      setTermsError(err.message);
      setTermsData(null);
    } finally {
      setTermsLoading(false);
    }
  };

  const handleClose = () => {
    setTermsData(null);
    setSelectedRow(null);
    setAddDialogOpen(false);
    setEditDialogOpen(false);
    setDeleteDialogOpen(false);
    setTermToDelete(null);
    setTermToDeleteType(null);
    setNewTerm('');
    setEditingTerm({ term: '', type: 'include' });
    onClose();
  };

  const handleEditClick = () => {
    if (!canEdit || !selectedRow) return;
    if (selectedRow) {
      setEditingTerm({
        term: selectedRow.term,
        type: selectedRow.type.toLowerCase()
      });
      setEditDialogOpen(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!canEdit || !selectedDocumentType || !selectedRow) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      // Remove the old term and add the new one
      const currentIncludeTerms = termsData?.includeTerms || [];
      const currentExcludeTerms = termsData?.excludeTerms || [];
      
      let newIncludeTerms = [...currentIncludeTerms];
      let newExcludeTerms = [...currentExcludeTerms];

      // Remove the old term
      if (selectedRow.type === 'Include') {
        newIncludeTerms = newIncludeTerms.filter(term => term !== selectedRow.term);
      } else {
        newExcludeTerms = newExcludeTerms.filter(term => term !== selectedRow.term);
      }

      // Add the updated term
      if (editingTerm.type === 'include') {
        newIncludeTerms.push(editingTerm.term);
      } else {
        newExcludeTerms.push(editingTerm.term);
      }

      // Always include both includeTerms and excludeTerms in payload
      const updateData = {
        includeTerms: newIncludeTerms,
        excludeTerms: newExcludeTerms
      };
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/${tenantId}/${selectedDocumentType.documentTypeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });
      
      if (!resp.ok) {
        // Try to parse error response
        let errorMessage = `Failed to update term: ${resp.status} ${resp.statusText}`;
        try {
          const errorData = await resp.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // If JSON parsing fails, use default message
        }
        throw new Error(errorMessage);
      }
      
      // Refresh the terms data
      await fetchTermsData(selectedDocumentType.documentTypeId);
      setEditDialogOpen(false);
      setSelectedRow(null);
      showSuccess('Term updated successfully');
    } catch (err) {
      console.error('Error updating term:', err);
      showError(err.message, 'Update Term Error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddTerm = async () => {
    if (!canAdd) return;
    if (!newTerm.trim() || !selectedDocumentType) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      
      // Always include both includeTerms and excludeTerms in payload
      const addData = {
        includeTerms: termType === 'include' ? [newTerm.trim()] : [],
        excludeTerms: termType === 'exclude' ? [newTerm.trim()] : []
      };
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/${tenantId}/${selectedDocumentType.documentTypeId}/append-terms`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(addData),
      });
      
      if (!resp.ok) {
        // Try to parse error response
        let errorMessage = `Failed to add term: ${resp.status} ${resp.statusText}`;
        try {
          const errorData = await resp.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // If JSON parsing fails, use default message
        }
        throw new Error(errorMessage);
      }
      
      // Refresh the terms data
      await fetchTermsData(selectedDocumentType.documentTypeId);
      setAddDialogOpen(false);
      setNewTerm('');
      showSuccess('Term added successfully');
    } catch (err) {
      console.error('Error adding term:', err);
      showError(err.message, 'Add Term Error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTerm = (term, termType) => {
    if (!canDelete) return;
    if (!selectedDocumentType || !term) return;
    setTermToDelete(term);
    setTermToDeleteType(termType);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDocumentType || !termToDelete) return;
    
    try {
      setSubmitting(true);
      setDeleteDialogOpen(false);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      // Get current terms
      const currentIncludeTerms = termsData?.includeTerms || [];
      const currentExcludeTerms = termsData?.excludeTerms || [];
      
      // Remove the term from the appropriate array
      let newIncludeTerms = [...currentIncludeTerms];
      let newExcludeTerms = [...currentExcludeTerms];

      if (termToDeleteType === 'Include') {
        newIncludeTerms = newIncludeTerms.filter(term => term !== termToDelete);
      } else {
        newExcludeTerms = newExcludeTerms.filter(term => term !== termToDelete);
      }

      // Always include both includeTerms and excludeTerms in payload
      const updateData = {
        includeTerms: newIncludeTerms,
        excludeTerms: newExcludeTerms
      };
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/${tenantId}/${selectedDocumentType.documentTypeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });
      
      if (!resp.ok) {
        // Try to parse error response
        let errorMessage = `Failed to delete term: ${resp.status} ${resp.statusText}`;
        try {
          const errorData = await resp.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // If JSON parsing fails, use default message
        }
        throw new Error(errorMessage);
      }
      
      // Refresh the terms data
      await fetchTermsData(selectedDocumentType.documentTypeId);
      setSelectedRow(null);
      setTermToDelete(null);
      setTermToDeleteType(null);
      showSuccess('Term deleted successfully');
    } catch (err) {
      console.error('Error deleting term:', err);
      showError(err.message, 'Delete Term Error');
    } finally {
      setSubmitting(false);
    }
  };



  useEffect(() => {
    if (open && selectedDocumentType) {
      fetchTermsData(selectedDocumentType.documentTypeId);
    }
  }, [open, selectedDocumentType]);

  const termsColumns = [
    { field: 'term', headerName: 'Term', flex: 1 },
    { 
      field: 'type', 
      headerName: 'Type', 
      width: 120,
      renderCell: (params) => {
        const isInclude = params.value === 'Include';
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 500,
              bgcolor: isInclude ? ht.secondaryTab : ht.listBg,
              color: isInclude ? ht.text : ht.text,
              borderRadius: ht.buttonRadius,
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        );
      }
    }
  ];

  const termsRows = [
    ...(termsData?.includeTerms || []).map((term, index) => ({
      id: `include-${index}`,
      term,
      type: 'Include'
    })),
    ...(termsData?.excludeTerms || []).map((term, index) => ({
      id: `exclude-${index}`,
      term,
      type: 'Exclude'
    }))
  ];

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 800 },
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: ht.drawerBg,
          },
        }}
      >
        <Box sx={{ ...harmonateDrawerHeaderSx, mx: 0, mt: 0, borderRadius: 0, mb: 0 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: ht.white }}>
              Document Terms: {selectedDocumentType?.documentTypeName}
            </Typography>
            <Typography variant="body2" sx={{ color: ht.secondaryTab }}>
              Document Type
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title={canEdit ? 'Edit Term' : 'No edit permission'}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleEditClick}
                  disabled={!selectedRow || submitting || !canEdit}
                  sx={harmonateDialogCloseIconSx}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canDelete ? 'Delete Term' : 'No delete permission'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (selectedRow) {
                      handleDeleteTerm(selectedRow.term, selectedRow.type);
                    }
                  }}
                  disabled={!selectedRow || submitting || !canDelete}
                  sx={harmonateDialogCloseIconSx}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canAdd ? 'Add Term' : 'No add permission'}>
              <span>
              <IconButton
                size="small"
                onClick={() => {
                  if (!canAdd) return;
                  setAddDialogOpen(true);
                }}
                disabled={!canAdd}
                sx={harmonateDialogCloseIconSx}
              >
                <AddIcon fontSize="small" />
              </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={handleClose} aria-label="Close drawer" sx={harmonateDialogCloseIconSx}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          {termsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress sx={{ color: ht.text }} />
              <Typography variant="body2" sx={{ ml: 2, color: ht.text }}>Loading terms...</Typography>
            </Box>
          ) : termsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {termsError}
            </Alert>
          ) : (
            <Paper
              elevation={0}
              sx={{
                ...harmonateCardSx,
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <DataGridPro
                columnResizeMode="onEnd"
                pagination
                rowsPerPageOptions={[10, 25, 50]}
                pageSize={10}
                autoPageSize={false}
                resizable
                rows={termsRows}
                columns={termsColumns}
                rowHeight={40}
                sx={{ 
                  ...harmonateDataGridSx,
                  flex: 1,
                  minHeight: 0,
                  '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab },
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                }}
                disableSelectionOnClick={false}
                getRowId={(row) => row.id}
                onRowClick={(params) => setSelectedRow(params.row)}
                rowSelectionModel={selectedRow ? [selectedRow.id] : []}
                onRowSelectionModelChange={(selection) => {
                  if (selection.length > 0) {
                    const selectedRowData = termsRows.find(row => row.id === selection[0]);
                    setSelectedRow(selectedRowData);
                  } else {
                    setSelectedRow(null);
                  }
                }}
                components={{
                  NoRowsOverlay: () => (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body1" sx={{ color: ht.text }}>
                        No terms available
                      </Typography>
                    </Box>
                  )
                }}
              />
            </Paper>
          )}
        </Box>
      </Drawer>

      {/* Add Term Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add New Term</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <TextField
            label="Term"
            value={newTerm}
            onChange={(e) => setNewTerm(e.target.value)}
            fullWidth
            required
            size="small"
            InputLabelProps={harmonateInputLabelProps}
          />
          <TextField
            label="Type"
            value={termType}
            onChange={(e) => setTermType(e.target.value)}
            select
            fullWidth
            size="small"
            InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            <MenuItem value="include">Include Term</MenuItem>
            <MenuItem value="exclude">Exclude Term</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddTerm} 
            variant="contained" 
            disabled={!newTerm.trim() || submitting}
            sx={harmonateProceedButtonSx}
          >
            {submitting ? 'Adding...' : 'Add Term'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Term Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Edit Term</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <TextField
            label="Term"
            value={editingTerm.term}
            onChange={(e) => setEditingTerm({ ...editingTerm, term: e.target.value })}
            fullWidth
            required
            size="small"
            InputLabelProps={harmonateInputLabelProps}
          />
          <TextField
            label="Type"
            value={editingTerm.type}
            onChange={(e) => setEditingTerm({ ...editingTerm, type: e.target.value })}
            select
            fullWidth
            size="small"
            InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            <MenuItem value="include">Include Term</MenuItem>
            <MenuItem value="exclude">Exclude Term</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            disabled={!editingTerm.term.trim() || submitting}
            sx={harmonateProceedButtonSx}
          >
            {submitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Are you sure you want to delete the term "{termToDelete}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            disabled={submitting}
            sx={harmonateAbortButtonSx}
          >
            {submitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
        variant={toastState.variant}
      />
    </>
  );
};

export default DocumentTypeTermsDrawer;
